import { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Project,
  SubmissionsByTask,
  Task,
  TasksLength,
} from '../page_components/PoolDetails/Missions/types'
import {
  selectCurrentProject,
  selectLeaderboard,
  selectTaskSubmissions,
  selectUserHasProject,
} from '../modules/missionControl/selectors'

import {
  setExpectedRealAllocation,
  setIsUserBlocked,
  setPointsRecords,
  setPricePerToken,
  setTaskSubmissions,
  setTotalAllocation,
  setTotalAllocationToAllParticipants,
  setUserHasProject,
  setUserProjects,
} from '../modules/missionControl/slice'
import { selectUser } from '../modules/user/selectors'
import { theseus } from '../api/theseus'
import { selectIsDarkThemeState } from '../modules/miscUi/selectors'
import {
  Frequency,
  findEarliestViableCompletionPeriod,
  getResetDateByFrequency,
  getStartDateByFrequency,
} from '../utils/recurring'
import { getMissionTaskType } from '../shared_components/Missions/MissionAction'

const useMissionControlPoolStateManagement = () => {
  const dispatch = useDispatch()

  const isDarkTheme = useSelector(selectIsDarkThemeState)
  const userHasProjectFromState = useSelector(selectUserHasProject)
  const currentProjectFromState = useSelector(selectCurrentProject)
  const taskSubmissionsFromState = useSelector(selectTaskSubmissions)
  const userFromState = useSelector(selectUser)
  const leaderboardFromState = useSelector(selectLeaderboard)

  const user = userFromState
  const hasProject = userHasProjectFromState

  const currentProject = currentProjectFromState
  const taskSubmissions = taskSubmissionsFromState
  const leaderboard = leaderboardFromState

  const projectSlugFromUrl = window.location.pathname.split('/')[2]

  const fetchUpdatedInformation = useCallback(async () => {
    if (!user?.id || !currentProject?.id) return

    const {
      taskSubmissions,
      pointsRecords,
      totalAllocation,
      totalAllocationToAllParticipants,
      expectedRealAllocation,
      pricePerToken,
      xpData,
      hasProject: hasProjectFromReq,
      isBlocked,
      projects: projectsFromReq,
    } = await fetchUserProjectPoolData(user.id, currentProject.id)

    dispatch(setUserHasProject(hasProjectFromReq))
    dispatch(setTaskSubmissions(taskSubmissions))
    dispatch(setPointsRecords(pointsRecords))
    dispatch(setTotalAllocation(totalAllocation))
    dispatch(
      setTotalAllocationToAllParticipants(totalAllocationToAllParticipants),
    )
    dispatch(setExpectedRealAllocation(expectedRealAllocation))
    dispatch(setPricePerToken(pricePerToken))
    dispatch(setIsUserBlocked(isBlocked))
    dispatch(setUserProjects(projectsFromReq))
  }, [user, currentProject, dispatch])

  useEffect(() => {
    if (!user?.id || !projectSlugFromUrl) return
    fetchUpdatedInformation()
  }, [user, dispatch, projectSlugFromUrl, fetchUpdatedInformation])

  const tasksLength: TasksLength = useMemo(
    () =>
      reduceToTasksLength(
        currentProject as Project,
        taskSubmissions ?? ({} as SubmissionsByTask),
      ),
    [currentProject, taskSubmissions],
  )

  const customBackground = isDarkTheme
    ? currentProject?.projectMedia?.topBanner?.darkTheme?.url
    : currentProject?.projectMedia?.topBanner?.lightTheme?.url

  return {
    currentProject,
    hasProject,
    customBackground,
    leaderboard,
    taskSubmissions,
    tasksLength,
  }
}

export default useMissionControlPoolStateManagement

export async function fetchUserProjectPoolData(
  accountId: number,
  projectId: number,
) {
  try {
    const promises = [
      theseus.get(`user/${accountId}/submissions/${projectId}/pool`),
    ]

    const [{ data }] = await Promise.all(promises)

    if (!data) throw new Error('No user submissions')

    if (!data) throw new Error('No data')

    const {
      submissions: unformattedTaskSubmissionsResponse,
      pointsRecords,
      totalAllocation,
      totalAllocationToAllParticipants,
      expectedRealAllocation,
      pricePerToken,
    } = data

    const unformattedTaskSubmissions = unformattedTaskSubmissionsResponse

    const taskSubmissions = formatSubmissions(unformattedTaskSubmissions)

    return {
      taskSubmissions,
      pointsRecords,
      totalAllocation,
      totalAllocationToAllParticipants,
      expectedRealAllocation,
      pricePerToken,
      xpData: {},
      hasProject: true,
      accountDetails: {},
      isBlocked: false,
      shopItems: [], // TODO: do we even need this?
      projects: [],
      competitions: [],
      wonPrizes: [],
      hasSignupBonus: !!data?.signupBonus?.id,
    }
  } catch (error) {
    console.error(error)
    return {
      taskSubmissions: {},
      pointsRecords: [],
      totalAllocation: 0,
      totalAllocationToAllParticipants: 0,
      expectedRealAllocation: 0,
      pricePerToken: 0,
      xpData: {},
      hasProject: false,
      accountDetails: {},
      isBlocked: false,
      shopItems: [],
      projects: [],
      competitions: [],
      wonPrizes: [],
      shouldLogout: true,
      hasSignupBonus: false,
    }
  }
}
export function filterDraftsAndEmptyObjectsFromTasks(tasks: Task[]) {
  return tasks
    .filter(Boolean)
    .filter((t) => !!Object.keys(t).length)
    .filter(({ published_on }) => !!published_on)
    .filter(({ availableFrom }) =>
      availableFrom
        ? new Date().getTime() >= new Date(availableFrom).getTime()
        : true,
    )
    .filter(
      ({ subtasks }) =>
        subtasks?.length &&
        subtasks.filter((st) => Object.keys(st).length).length,
    )
}

export function reduceToTasksLength(
  currentProject: Project,
  taskSubmissions: SubmissionsByTask,
) {
  return (
    filterDraftsAndEmptyObjectsFromTasks(currentProject?.tasks ?? []).reduce(
      (acc, task) => {
        if (!task) return acc
        if (!task?.published_on) return acc

        const { isActive, isPending, isExpired, isComplete } =
          getTaskSubmissionStatus({ task, taskSubmissions })

        acc.all += 1

        if (isPending) {
          acc.pending += 1
          return acc
        }

        if (isComplete) {
          acc.complete += 1
          return acc
        }

        if (isActive) {
          acc.active += 1
          return acc
        }

        if (isExpired) acc.expired += 1

        return acc
      },
      {
        all: 0,
        active: 0,
        complete: 0,
        expired: 0,
        pending: 0,
      },
    ) ?? { all: 0, active: 0, complete: 0, expired: 0, pending: 0 }
  )
}

export function groupTasksAndOrder(
  tasks: Task[],
  taskSubmissions: SubmissionsByTask,
) {
  const [active, pending, complete, activeLinkTasks, expired] = tasks.reduce(
    (acc, task) => {
      if (!task) return acc
      // if (!task?.published_on) return acc
      task = { ...task }

      const coreTask = task.subtasks?.[0]
      if (!coreTask) return acc

      const { isActive, isPending, isExpired, isComplete } =
        getTaskSubmissionStatus({ task, taskSubmissions })

      const { visitUrl, imageVerification, requiresSocial, isEmailTask } =
        coreTask

      const isLinkOnly =
        !!visitUrl && !imageVerification && !requiresSocial && !isEmailTask

      const [active, pending, complete, activeLinkTasks, expired] = acc

      if (isComplete) {
        task.status = 'complete'
        complete.push(task)
        return acc
      }

      if (isActive) {
        task.status = 'active'

        if (isLinkOnly) {
          activeLinkTasks.push(task)
          return acc
        }
        active.push(task)
        return acc
      }

      if (isExpired) {
        task.status = 'expired'
        expired.push(task)
        return acc
      }

      if (isPending) {
        task.status = 'pending'
        pending.push(task)
        return acc
      }

      return acc
    },
    [[], [], [], [], []] as [Task[], Task[], Task[], Task[], Task[]],
  )

  const ascendingCompletedOn = (a: Task, b: Task) => {
    const currentSubmissions = taskSubmissions?.[a?.id]
    const latestSubmission1 =
      currentSubmissions?.[currentSubmissions?.length - 1]
    const currentSubmissions2 = taskSubmissions?.[b?.id]
    const latestSubmission2 =
      currentSubmissions2?.[currentSubmissions2?.length - 1]
    const aDate =
      latestSubmission1?.published_at &&
      new Date(latestSubmission1?.published_at).getTime()

    const bDate =
      latestSubmission2?.published_at &&
      new Date(latestSubmission2?.published_at).getTime()

    if (aDate && !bDate) return -1
    if (!aDate && bDate) return 1

    if (aDate && bDate) {
      return Number(aDate - bDate)
    }

    return 0
  }

  const expiredTasks = [...expired.sort(ascendingCompletedOn)]

  const completedTasks = [
    ...pending.sort(taskSorter),
    ...complete.sort(ascendingCompletedOn),
  ]

  return [
    [...active.sort(taskSorter)],
    [...activeLinkTasks.sort(taskSorter)],
    completedTasks,
    expiredTasks,
  ]
}

export function getTaskSubmissionStatus({
  task,
  taskSubmissions,
}: {
  task: Task
  taskSubmissions: SubmissionsByTask
}) {
  task = { ...task }
  const subtaskId = task?.subtasks[0]?.id
  const currentSubmissions = taskSubmissions?.[subtaskId]
  const { isZapLaunchMission, isManualAPIRewardWithLink } = getMissionTaskType({ task })

  const isInstantTask = true // marked feedbakc not happening anymore

  const hasCurrentSubmission = !!currentSubmissions?.length

  const notCompleted = !currentSubmissions?.every((s) => s.hasCompleted)

  const noSubtasks = !currentSubmissions?.some((s) => s.subtasks?.length)

  const allSubtasksAttempted = currentSubmissions?.every(
    (s) => s.subtasks?.length === task?.subtasks?.length,
  )

  const allSubtasksCompleted = currentSubmissions?.every((s) =>
    s.subtasks?.every((st) => st.hasCompleted),
  )

  let hasCompleted = !notCompleted || (!noSubtasks && allSubtasksCompleted)

  if (task.frequency !== Frequency.NONE) {
    if (isZapLaunchMission) {
      const earliestCompletionPeriod = findEarliestViableCompletionPeriod(
        task.frequency,
      )

      const submissionsInCurrentPeriod = currentSubmissions?.filter((s) => {
        const createdAt = new Date(s.createdAt)
        return createdAt >= earliestCompletionPeriod
      })

      hasCompleted = submissionsInCurrentPeriod?.length >= task.completionsPerPeriod
    } else {
      const startDate = new Date(getStartDateByFrequency(task.frequency))
      const endDate = new Date(getResetDateByFrequency(task.frequency))
      const filteredSubmissions = currentSubmissions?.filter((s) => {
        const createdAt = new Date(s.createdAt)
        return createdAt >= startDate && createdAt <= endDate
      })
  
      hasCompleted = filteredSubmissions?.length >= task.completionsPerPeriod
    }
  }

  const isPendingNoSubtasks =
    (hasCurrentSubmission && notCompleted && noSubtasks) ||
    (hasCurrentSubmission && !isInstantTask && noSubtasks) ||
    (hasCurrentSubmission && !hasCompleted && !isInstantTask && noSubtasks)

  const isPendingSubtasks =
    (hasCurrentSubmission &&
      !noSubtasks &&
      allSubtasksAttempted &&
      !allSubtasksCompleted) ||
    (!noSubtasks && notCompleted && allSubtasksAttempted)

  const isPending =
    !hasCompleted && Boolean(isPendingNoSubtasks || isPendingSubtasks)
  // TODO: Support subtasks
  // && !currentSubmission?.subtasks?.[0]?.shouldTriggerAnimation

  const isExpired = Boolean(
    task?.expiresOn &&
      new Date(task?.expiresOn).getTime() < new Date().getTime() &&
      !hasCompleted,
  )

  const isActive = Boolean(
    !isExpired && !hasCompleted,
    // TODO: support subtasks pending
    // && !isPending
  )

  if (isManualAPIRewardWithLink) {
    const completedManualApiMissions = localStorage.getItem('completedManualApiMissions')
    const completedManualApiMissionsArray = completedManualApiMissions ? JSON.parse(completedManualApiMissions) : []
    hasCompleted = completedManualApiMissionsArray.includes(task.id)
  }
  
  // TODO: support subtasks pending
  // const isComplete = Boolean(hasCompleted || isPending)
  const isComplete = Boolean(hasCompleted)

  return {
    isPending,
    isExpired,
    isActive,
    isComplete,
  }
}

export const descendingPublishedOn = (a: Task, b: Task) => {
  if (!a || !b) return 0
  if (!a?.published_on || !b?.published_on) return 0
  const aDate = new Date(a?.published_on).getTime()
  const bDate = new Date(b?.published_on).getTime()
  if (aDate > bDate) return -1
  if (bDate > aDate) return 1
  return 0
}

export const ascendingPublishedOn = (a: Task, b: Task) => {
  if (!a || !b) return 0
  if (!a?.published_on || !b?.published_on) return 0
  const aDate = new Date(a?.published_on).getTime()
  const bDate = new Date(b?.published_on).getTime()
  if (aDate > bDate) return 1
  if (bDate > aDate) return -1
  return 0
}

export function taskSorter(a: Task, b: Task) {
  const aOrder = a?.order
  const bOrder = b?.order
  const aCompleted = a.status === 'complete'
  const bCompleted = b.status === 'complete'
  if (aCompleted && !bCompleted) return 1
  if (!aCompleted && bCompleted) return -1
  if (typeof aOrder === 'number' && typeof bOrder === 'number') {
    return aOrder - bOrder
  }
  if (typeof aOrder === 'number' && typeof bOrder !== 'number') {
    return -1
  }
  if (typeof aOrder !== 'number' && typeof bOrder === 'number') {
    return 1
  }
  return 0
}

export function formatSubmissions(submissions: any) {
  if (!submissions?.length) return {}

  const submissionsByTaskId: Record<string, SubmissionsByTask[]> = {}
  for (let j = 0; j < submissions.length; j++) {
    const {
      id,
      taskId,
      hasCompleted,
      completionMethod,
      scoreOutOfTen,
      published_at,
      isSubtask,
      createdAt,
      periodStartDate,
      completionsCount,
    } = submissions[j]

    const score =
      scoreOutOfTen === null
        ? completionMethod !== 'oneToTenScore' && isSubtask && hasCompleted
          ? 10
          : 0
        : scoreOutOfTen

    const submission = {
      submissionId: id,
      hasCompleted,
      score: hasCompleted ? score : 0,
      periodStartDate,
      completionsCount,
      published_at,
      createdAt,
    }

    if (!submissionsByTaskId?.[taskId]) {
      submissionsByTaskId[taskId] = [submission]
    } else {
      submissionsByTaskId[taskId] = [...submissionsByTaskId[taskId], submission]
    }
  }

  return submissionsByTaskId
}
